import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CameraAliasId, NormPoint, PublicSourceView, SourceMode, GameSelection, VisionCalibration } from '@urbanrats/contracts';
import { hashCanonical } from '@urbanrats/domain';
import { CalibrationPanel, applyPick, type PickMode } from './components/CalibrationPanel.tsx';
import { defaultCalibration, loadCalibration, saveCalibration } from './lib/calibration.ts';
import { gameTitleKey } from './lib/gameText.ts';
import { DevPanel } from '#dev-tools';
import { BetPanel } from './components/BetPanel.tsx';
import { CountPanel } from './components/CountPanel.tsx';
import { Dialog } from './components/Dialog.tsx';
import { EvidencePanel } from './components/EvidencePanel.tsx';
import { HistoryPanel } from './components/HistoryPanel.tsx';
import { MediaPlayer, type FrameInfo, type PlayerControls, type PlayerSource, type PlayerStatus } from './components/MediaPlayer.tsx';
import { SourcePanel } from './components/SourcePanel.tsx';
import { VideoStatusPanel } from './components/VideoStatusPanel.tsx';
import { RecentResults } from './components/RecentResults.tsx';
import { RulesIntro } from './components/RulesIntro.tsx';
import { RulesPanel } from './components/RulesPanel.tsx';
import { SettingsPanel } from './components/SettingsPanel.tsx';
import { WalletPanel } from './components/WalletPanel.tsx';
import { RewardsPanel } from './components/RewardsPanel.tsx';
import { DropChest, RanksPanel } from './components/Engagement.tsx';
import { DisplayContext, loadDisplayPrefs, saveDisplayPrefs, useApprox, type DisplayPrefs } from './lib/fx.ts';
import { TopBar } from './components/TopBar.tsx';
import { VideoStage, type TestPattern } from './components/VideoStage.tsx';
import { LiveStage } from './components/LiveStage.tsx';
import type { ClientGateways, OverlayTrack, VideoCountingHandle, VisionStatus } from './gateway/index.ts';
import { I18nContext, makeI18n, type LocaleCode, type MessageKey } from './i18n/index.ts';
import { enableAudio, setVolume as setSoundVolume, sounds } from './sound.ts';
import { useGame, useNow, usePrefersReducedMotion } from './useGame.ts';
import { useEmbed } from './useEmbed.ts';
import type { EmbedHandle } from './embed.ts';

type Panel = 'history' | 'rules' | 'settings' | 'source' | 'wallet' | 'rewards' | 'ranks' | { evidence: string } | null;

export type ActiveSource =
  | { kind: 'simulation' }
  | {
      kind: 'video';
      id: string;
      label: string;
      cameraAlias: CameraAliasId | null;
      sourceMode: SourceMode;
      attribution: string | null;
      geometry: PublicSourceView['previewGeometry'];
      player: PlayerSource;
    };

function fromView(v: PublicSourceView): ActiveSource {
  const player: PlayerSource =
    v.mediaType === 'VIDEO_WEBCAM'
      ? { kind: 'webcam', sourceId: v.sourceId }
      : { kind: v.playbackKind === 'hls' ? 'hls' : 'file', sourceId: v.sourceId, url: v.playbackPath ?? '', maxSourceAgeMs: v.maxSourceAgeMs, loop: v.playbackKind !== 'hls' };
  return { kind: 'video', id: v.sourceId, label: v.displayName, cameraAlias: v.mediaType === 'VIDEO_FILE' ? v.cameraAlias : null, sourceMode: v.sourceMode, attribution: v.attribution, geometry: v.previewGeometry, player };
}

const PREF_KEY = 'urbanrats.prefs.v1';
function loadPrefs(): { locale: LocaleCode; muted: boolean; volume: number } {
  try {
    const p = JSON.parse(localStorage.getItem(PREF_KEY) ?? '{}') as { locale?: string; muted?: boolean; volume?: number };
    return { locale: p.locale === 'tr' ? 'tr' : 'en', muted: p.muted !== false, volume: typeof p.volume === 'number' && p.volume >= 0 && p.volume <= 1 ? p.volume : 1 };
  } catch {
    return { locale: 'en', muted: true, volume: 1 };
  }
}

const ACTIVE = new Set(['ACCEPTED', 'RESULT_WON', 'RESULT_LOST', 'CREDIT_PENDING', 'SETTLED']);

export function App({ gw, onLobby, embed = null }: { gw: ClientGateways; onLobby?: () => void; embed?: EmbedHandle | null }) {
  const game = useGame(gw);
  const now = useNow(gw);
  const reducedMotion = usePrefersReducedMotion();
  const [prefs] = useState(loadPrefs);
  const [locale, setLocale] = useState<LocaleCode>(prefs.locale);
  const [muted, setMuted] = useState(prefs.muted);
  const [volume, setVolume] = useState(prefs.volume);
  const [panel, setPanel] = useState<Panel>(null);
  const [testPattern, setTestPattern] = useState<TestPattern | null>(null);
  const [active, setActive] = useState<ActiveSource>({ kind: 'simulation' });
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus | null>(null);
  const [playerKey, setPlayerKey] = useState(0);
  const playerControls = useRef<PlayerControls>(null);
  const timecodeRef = useRef<HTMLSpanElement>(null);
  const counting = useRef<VideoCountingHandle | null>(null);
  const tracksRef = useRef<((t: OverlayTrack[]) => void) | null>(null);
  const [vision, setVision] = useState<VisionStatus | null>(null);
  const [savedCal, setSavedCal] = useState<{ id: string; cal: VisionCalibration } | null>(null);
  const [draft, setDraft] = useState<VisionCalibration | null>(null);
  const [pickMode, setPickMode] = useState<PickMode>(null);
  const lineClicks = useRef(0);
  const i18n = useMemo(() => makeI18n(locale), [locale]);
  const { t } = i18n;
  const [displayPrefs, setDisplayPrefsState] = useState<DisplayPrefs>(loadDisplayPrefs);
  const approx = useApprox(gw, displayPrefs.displayCurrency, now);
  const display = useMemo(
    () => ({
      prefs: displayPrefs,
      setPrefs: (p: DisplayPrefs) => {
        setDisplayPrefsState(p);
        saveDisplayPrefs(p);
      },
      approx,
    }),
    [displayPrefs, approx],
  );
  const promoFlags = gw.promo?.flags;
  const showRewards = !!promoFlags && (promoFlags.inventory || promoFlags.level || promoFlags.dailyCheckin || promoFlags.lightning);

  const videoMode = active.kind === 'video';
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [picture, setPicture] = useState<{ wallMs: number | null; behindMs: number | null }>({ wallMs: null, behindMs: null });
  useEffect(() => {
    gw.media.setVideoMode(videoMode);
  }, [gw, videoMode]);

  // Attach video counting to registry file sources that have a calibration (the worker decides support).
  const countingKey = active.kind === 'video' && active.cameraAlias && active.geometry ? `${active.id}#${playerKey}` : null;
  const calKey = `${gw.gameInfo.gameId}:${active.kind === 'video' ? active.id : ''}`;
  const baseCalibration = useMemo(
    () => (active.kind === 'video' && active.geometry ? (loadCalibration(calKey) ?? defaultCalibration(active.geometry, gw.gameInfo.includedClasses)) : null),
    [active, calKey, gw.gameInfo.includedClasses],
  );
  const calibration = active.kind === 'video' && savedCal?.id === active.id ? savedCal.cal : baseCalibration;
  useEffect(() => {
    if (active.kind !== 'video' || !active.cameraAlias || !baseCalibration) return;
    const cal = baseCalibration;
    const h = gw.media.startVideoCounting({ sourceId: active.id, cameraAlias: active.cameraAlias, sourceMode: active.sourceMode, calibration: cal });
    counting.current = h;
    const u1 = h.onStatus(setVision);
    const u2 = h.onTracks((tracks) => tracksRef.current?.(tracks));
    return () => {
      u1();
      u2();
      h.stop();
      counting.current = null;
      setVision(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by source + player instance
  }, [gw, countingKey]);

  useEffect(() => {
    if (playerStatus) counting.current?.reportHealth(playerStatus.health.status, playerStatus.health.reasons.join(',') || 'ok');
  }, [playerStatus?.health.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPick = (p: NormPoint) => {
    if (!draft || !pickMode) return;
    const r = applyPick(draft, pickMode, p, lineClicks.current);
    if (pickMode === 'line') lineClicks.current++;
    setDraft(r.draft);
    if (r.done) setPickMode(null);
  };

  const onFrame = useCallback((f: FrameInfo) => {
    counting.current?.presented(f);
    const el = timecodeRef.current;
    if (el) {
      el.textContent = `PTS ${(f.ptsMicros / 1e6).toFixed(2)} s`;
      el.dataset.pts = String(f.ptsMicros);
      el.dataset.epoch = f.sourceEpoch;
    }
  }, []);

  const chooseSimulation = () => {
    setActive({ kind: 'simulation' });
    setPlayerStatus(null);
    setPanel(null);
  };
  const chooseSource = (v: PublicSourceView) => {
    setActive(fromView(v));
    setPlayerStatus(null);
    setPanel(null);
  };
  const chooseUserFile = (file: File, attribution: string) => {
    setActive({
      kind: 'video',
      id: `user-file`,
      label: file.name,
      cameraAlias: null,
      sourceMode: 'RECORDED_DEMO',
      attribution: attribution || null,
      geometry: null,
      player: { kind: 'blob', sourceId: `user-file-${file.name}`, file, loop: true },
    });
    setPlayerStatus(null);
    setPanel(null);
  };

  useEffect(() => {
    document.documentElement.lang = locale === 'pseudo' ? 'en' : locale === 'rtl' ? 'he' : locale;
    document.documentElement.dir = i18n.dir;
    if (locale === 'pseudo' || locale === 'rtl') return;
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ locale, muted, volume }));
    } catch {
      /* preferences are a convenience only */
    }
  }, [locale, muted, volume, i18n.dir]);
  useEffect(() => setSoundVolume(volume), [volume]);
  useEmbed(embed, gw, game, { muted, setMuted, volume, setVolume, panel: typeof panel === 'object' && panel !== null ? 'evidence' : panel, setPanel });

  useEffect(() => {
    if (!muted && game.countPulse > 0) sounds.count();
  }, [game.countPulse, muted]);

  const round = game.round;
  // LIVE round whose picture has not yet reached the end of its window: the screen must not run ahead of it.
  const pictureBehind = !!round && round.manifest.sourceMode === 'LIVE' && picture.wallMs !== null && picture.wallMs < Date.parse(round.manifest.timing.captureEndAt) && !['VOIDED', 'VOIDING'].includes(round.state);
  // Result sound when the result is shown (for LIVE: once the picture has reached the end of the window):
  // a short win arpeggio if one of this player's bets returned money, else the plain result cue.
  const shownOutcome = pictureBehind ? null : game.outcome;
  // LIVE: the numbers of the bet board belong to one round. They disappear once its window is over on screen and
  // the new round's numbers appear with the globe intro (when the map lands on the new country).
  const [introPlaying, setIntroPlaying] = useState(false);
  const liveRound = !!round && round.manifest.sourceMode === 'LIVE';
  const windowOver =
    liveRound &&
    !['SCHEDULED', 'BETTING_OPEN'].includes(round.state) &&
    (picture.wallMs !== null ? picture.wallMs >= Date.parse(round.manifest.timing.captureEndAt) : ['VERIFYING', 'RESULT_FINAL', 'SETTLING', 'SETTLED', 'VOIDING', 'VOIDED'].includes(round.state));
  const boardConcealed = liveRound && (introPlaying || windowOver);
  useEffect(() => {
    if (muted || !shownOutcome) return;
    const won = game.bets.some((b) => b.gameRoundId === shownOutcome.gameRoundId && b.payoutGross !== null && BigInt(b.payoutGross.amountAtomic) > 0n);
    if (won) sounds.win();
    else if (shownOutcome.kind === 'RESULT') sounds.result();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownOutcome?.gameRoundId, shownOutcome?.kind, muted]);
  const roundBets = useMemo(() => (round ? game.bets.filter((b) => b.gameRoundId === round.gameRoundId) : []), [game.bets, round]);
  const lastRoundLines = useMemo(() => {
    const prev = game.bets.find((b) => b.gameRoundId !== round?.gameRoundId && ACTIVE.has(b.state));
    if (!prev) return null;
    const lines: Partial<Record<GameSelection, string>> = {};
    for (const b of game.bets)
      if (b.gameRoundId === prev.gameRoundId && ACTIVE.has(b.state)) lines[b.selection] = (BigInt(lines[b.selection] ?? '0') + BigInt(b.stake.amountAtomic)).toString();
    return lines;
  }, [game.bets, round?.gameRoundId]);

  const sourceMode: SourceMode = active.kind === 'video' ? active.sourceMode : (round?.manifest.sourceMode ?? 'SIMULATION');
  // A video round is one opened by the engine for this source with inference counting.
  const videoRound = videoMode && vision?.state === 'ready' && round && round.manifest.captureWindowPts !== null ? round : null;
  const mediaBlock =
    playerStatus && !playerStatus.health.bettable
      ? t('bet.mediaBlocked', {
          reason: playerStatus.error ? t(`video.error.${playerStatus.error}`) : playerStatus.health.reasons.map((r) => t(`video.health.${r}`)).join(', '),
        })
      : null;
  const videoBlocked =
    mediaBlock ??
    (!countingKey ? t('bet.noCounter') : vision?.state === 'ready' ? t('vision.waitingRound') : t(`vision.${vision?.state ?? 'connecting'}` as MessageKey));
  const shownGeometry = draft ?? (videoRound ? videoRound.manifest : calibration ?? (active.kind === 'video' ? active.geometry : null));

  return (
    <I18nContext.Provider value={i18n}>
      <DisplayContext.Provider value={display}>
      <div className={`app ${reducedMotion ? 'reduced-motion' : ''}`} dir={i18n.dir}>
        <TopBar
          round={videoMode ? videoRound : round}
          sourceMode={sourceMode}
          connection={game.connection}
          balance={game.balance}
          onOpen={setPanel}
          gameTitle={t(gameTitleKey(gw.gameInfo.gameId))}
          onLobby={onLobby}
          showRewards={showRewards}
          showRanks={!!gw.engagement}
          funPlay={gw.funPlay === true}
          realMoney={gw.session.moneyMode === 'REAL'}
          muted={muted}
          onToggleSound={() => {
            if (muted) enableAudio();
            setMuted(!muted);
          }}
        />
        {game.connection !== 'connected' && (
          <div className="banner" role="alert" data-testid="conn-banner">
            {t('conn.banner')}
          </div>
        )}
        <main className="layout">
          <div className="stage-col">
            {gw.engagement && <DropChest gw={gw} nowMs={now} />}
            {active.kind === 'video' ? (
              <>
                <VideoStage
                  geometry={shownGeometry}
                  sourceMode={active.sourceMode}
                  testPattern={null}
                  pulse={videoRound ? game.countPulse : 0}
                  reducedMotion={reducedMotion}
                  media={{
                    node: <MediaPlayer key={`${active.id}-${playerKey}`} source={active.player} onStatus={setPlayerStatus} onFrame={onFrame} controlsRef={playerControls} label={active.label} />,
                    intrinsic: playerStatus && playerStatus.width > 0 ? { w: playerStatus.width, h: playerStatus.height } : null,
                  }}
                  hud={
                    <span className="hud-chip mono" data-testid="timecode" ref={timecodeRef}>
                      PTS —
                    </span>
                  }
                  notice={draft ? t('cal.editing') : videoRound ? null : shownGeometry ? t('cal.notLocked') : null}
                  lineLocked={!!videoRound && !draft}
                  onPick={pickMode ? onPick : null}
                  tracksRef={tracksRef}
                />
                {videoRound && <CountPanel round={videoRound} now={now} outcome={game.outcome} roundBets={roundBets} pulse={game.countPulse} />}
                {draft && (
                  <CalibrationPanel
                    draft={draft}
                    setDraft={setDraft}
                    pickMode={pickMode}
                    setPickMode={(m) => {
                      lineClicks.current = 0;
                      setPickMode(m);
                    }}
                    savedHash={calibration ? hashCanonical(calibration) : null}
                    onSave={() => {
                      setSavedCal({ id: active.id, cal: draft });
                      saveCalibration(calKey, draft);
                      counting.current?.setCalibration(draft);
                      setDraft(null);
                    }}
                    onCancel={() => {
                      setDraft(null);
                      setPickMode(null);
                    }}
                  />
                )}
                <VideoStatusPanel
                  status={playerStatus}
                  vision={countingKey ? vision : null}
                  sourceLabel={active.label}
                  attribution={active.attribution}
                  onRetry={() => setPlayerKey((k) => k + 1)}
                  onBack={chooseSimulation}
                  onCalibrate={calibration && !draft ? () => setDraft(calibration) : null}
                />
              </>
            ) : (
              <>
                {round?.manifest.camera ? (
                  <LiveStage round={round} gw={gw} reducedMotion={reducedMotion} onLiveCount={setLiveCount} onPicture={setPicture} onIntro={setIntroPlaying} outcome={game.outcome} soundOn={!muted} />
                ) : (
                  <VideoStage geometry={round?.manifest ?? null} sourceMode={sourceMode} testPattern={testPattern} pulse={game.countPulse} reducedMotion={reducedMotion} />
                )}
                {round && (
                  <CountPanel
                    // Live-camera rounds: the count follows the footage (server-decided events revealed at their video time).
                    // LIVE: until the picture has passed the window end, show what the picture shows (no early result).
                    round={
                      pictureBehind
                        ? { ...round, count: liveCount ?? 0, finalCount: null, winningSelections: null }
                        : round.manifest.camera && liveCount !== null && round.finalCount === null
                          ? { ...round, count: liveCount }
                          : round
                    }
                    now={now}
                    pictureMs={picture.wallMs}
                    concealBands={liveRound && introPlaying}
                    outcome={pictureBehind ? null : game.outcome}
                    roundBets={roundBets}
                    pulse={game.countPulse}
                  />
                )}
              </>
            )}
            {/* Last results sit right under the count panel, as wide as it. */}
            {(videoRound ?? (!videoMode ? round : null)) && round && <RecentResults recent={game.recent} rules={round.manifest.rules} />}
          </div>
          <aside className="bet-col">
            {videoRound && (
              <BetPanel key={videoRound.gameRoundId} gw={gw} round={videoRound} balance={game.balance} connection={game.connection} roundBets={roundBets} lastRoundLines={null} extraBlock={mediaBlock} />
            )}
            {videoMode && !videoRound && (
              <section className="bet-panel" data-testid="bet-panel" aria-label={t('bet.title')}>
                <h2>{t('bet.title')}</h2>
                <p className="blocked" role="status" data-testid="bet-blocked">
                  {videoBlocked}
                </p>
              </section>
            )}
            {!videoMode && round && (
              <BetPanel
                key={round.gameRoundId}
                gw={gw}
                round={round}
                balance={game.balance}
                connection={game.connection}
                roundBets={roundBets}
                lastRoundLines={lastRoundLines}
                concealed={boardConcealed}
                extraBlock={game.walletDown ? t('wallet.down') : null}
              />
            )}
          </aside>
        </main>

        <RulesIntro gw={gw} onLeave={onLobby} />
        <Dialog open={panel === 'history'} title={t('history.title')} onClose={() => setPanel(null)} testId="dlg-history">
          <HistoryPanel gw={gw} bets={game.bets} onEvidence={(id) => setPanel({ evidence: id })} />
        </Dialog>
        <Dialog open={panel === 'source'} title={t('source.title')} onClose={() => setPanel(null)} testId="dlg-source">
          {panel === 'source' && (
            <SourcePanel gw={gw} suitable={gw.gameInfo.videoSources} activeId={active.kind === 'video' ? active.id : 'simulation'} onSimulation={chooseSimulation} onSource={chooseSource} onUserFile={chooseUserFile} />
          )}
        </Dialog>
        <Dialog open={panel === 'wallet'} title={t('wallet.title')} onClose={() => setPanel(null)} testId="dlg-wallet">
          {panel === 'wallet' && <WalletPanel gw={gw} balance={game.balance} nowMs={now} setLocale={setLocale} />}
        </Dialog>
        <Dialog open={panel === 'rewards'} title={t('rewards.title')} onClose={() => setPanel(null)} testId="dlg-rewards">
          {panel === 'rewards' && <RewardsPanel gw={gw} />}
        </Dialog>
        <Dialog open={panel === 'ranks'} title={t('ranks.title')} onClose={() => setPanel(null)} testId="dlg-ranks">
          {panel === 'ranks' && <RanksPanel gw={gw} />}
        </Dialog>
        <Dialog open={panel === 'rules'} title={t('rules.title')} onClose={() => setPanel(null)} testId="dlg-rules">
          <RulesPanel round={round} game={gw.gameInfo} realMoney={gw.session.moneyMode === 'REAL'} />
        </Dialog>
        <Dialog open={panel === 'settings'} title={t('settings.title')} onClose={() => setPanel(null)} testId="dlg-settings">
          <SettingsPanel
            gw={gw}
            locale={locale}
            setLocale={setLocale}
            muted={muted}
            setMuted={(m) => {
              if (!m) enableAudio();
              setMuted(m);
            }}
            reducedMotion={reducedMotion}
          />
        </Dialog>
        <Dialog open={typeof panel === 'object' && panel !== null} title={t('evidence.title')} onClose={() => setPanel('history')} testId="dlg-evidence">
          {typeof panel === 'object' && panel !== null && <EvidencePanel gw={gw} gameRoundId={panel.evidence} />}
        </Dialog>
        <Dialog open={game.sessionExpired} title={t('session.title')} onClose={() => undefined} variant="modal" dismissible={false} testId="dlg-session">
          <p>{t('session.body')}</p>
          {gw.navigation?.lobbyUrl ? (
            // The operator's lobby: a new launch comes from the operator's site (top window when embedded).
            <a className="confirm" href={gw.navigation.lobbyUrl} target="_top" rel="noopener" data-testid="session-lobby">
              {t('session.lobby')}
            </a>
          ) : (
            <button type="button" className="confirm" onClick={() => window.location.reload()}>
              {t('session.reload')}
            </button>
          )}
        </Dialog>

        <DevPanel gw={gw} testPattern={testPattern} setTestPattern={setTestPattern} locale={locale} setLocale={setLocale} player={playerControls} activeSourceId={active.kind === 'video' ? active.id : null} />
      </div>
      </DisplayContext.Provider>
    </I18nContext.Provider>
  );
}
